import { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaPlus,
  FaSlidersH,
  FaEllipsisH,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUsers,
  FaHardHat,
  FaBriefcase,
  FaStore,
  FaChevronDown,
  FaTimes,
  FaFilter,
} from "react-icons/fa";
import Partner, { PartnerType } from "../models/Partner";
import { useModal } from "../context/ConfirmModalContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import FullPageSpinner from "../components/FullPageSpinner";
import { sendRequest } from "../utils/api";
import { v4 as uuidv4 } from "uuid";
import { capitalizeFirst } from "../utils/string";
import { initAllStores } from "../utils/cache";
import { Input, PartnerMenu, StatCard } from "../components/Partner";
import PartnerDetailsModal from "../Modal/PartnerDetailsModal";
import User from "../models/User";
import { FilterChip, FilterMenuItem, FilterOption, getDateLabel } from "../components/FilterComponents";
import Pagination from "../components/Pagination";


const typeStyles: Record<PartnerType, string> = {
  artisan: "bg-orange-50 text-orange-700 border-orange-100",
  consultant: "bg-purple-50 text-purple-700 border-purple-100",
  vendor: "bg-blue-50 text-blue-700 border-blue-100",
};

const getInitials = (name: string): string => {
  const names = name.trim().split(/\s+/);

  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }

  return names[0].slice(0, 2).toUpperCase();
};

function Partners() {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();

  // const [activeTab, setActiveTab] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isUpdate, setIsUpdate] = useState(false);

  const [user, setUser] = useState<User>({} as User)
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  const [filter, setFilter] = useState({
    date: "today", type: "all", status: "all"
  });

  const [showFilters, setShowFilters] = useState(false);
  const [activeFilterMenu, setActiveFilterMenu] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });


  const filteredPartners = useMemo(() => {
    const query = search.trim().toLowerCase();

    return partners.filter((partner) => {
      // Type filter
      const matchesType =
        filter.type === "all" ||
        partner.type?.toLowerCase() === filter.type.toLowerCase();

      // Status filter
      const matchesStatus =
        filter.status === "all" ||
        (filter.status === "active" && partner.isActive === true) ||
        (filter.status === "inactive" && partner.isActive === false);

      // Search filter
      const matchesSearch =
        !query ||
        partner.name?.toLowerCase().includes(query) ||
        partner.companyName?.toLowerCase().includes(query) ||
        partner.specialty?.toLowerCase().includes(query) ||
        partner.address?.toLowerCase().includes(query) ||
        partner.phone?.toLowerCase().includes(query) ||
        partner.email?.toLowerCase().includes(query);

      return matchesType && matchesStatus && matchesSearch;
    });
  }, [partners, search, filter]);

  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);


  const paginatedPartners = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredPartners.slice(startIndex, endIndex);
  }, [filteredPartners, page, itemsPerPage]);



  useEffect(() => {
    const getPartners = async () => {
      try {
        const cache = await initAllStores();
        setLoading(true);

        const user = await cache.getBy("data", { id: "user" });

        const partners = await cache.getAllBy("partners");
        setPartners(partners);

        setUser({ ...user, id: user.userId, });

      } catch (err: any) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
    getPartners();
  }, []);


  const addPartner = async (data: Partner) => {
    return openModal(
      "Create New Partner",
      "You’re about to create this partner. Do you want to continue?",
      async () => {
        setLoading(true);

        const cache = await initAllStores();

        try {
          await sendRequest("POST", "/partners", data);

          showToast("Partner created successfully.", {
            type: "success",
          });

          const partnerData = { ...data, user };
          setShowAddModal(false);
          setPartners(prev => [...prev, partnerData]);
          await cache.saveOne("partners", partnerData);

        } catch (err: any) {
          console.error("Error creating partner:", err);

          showToast(
            err?.message ||
            "Failed to create partner. Please check your network and try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
        }
      },
      true,
      "Yes, Create",
      "Cancel"
    );
  };


  const updatePartner = async (data: Partner) => {
    return openModal(
      "Update Partner",
      "You’re about to update this partner. Do you want to continue?",
      async () => {
        setLoading(true);

        const cache = await initAllStores();

        try {
          await sendRequest("PUT", `/partners/${data.id}`, data);

          showToast("Partner updated successfully.", {
            type: "success",
          });

          const partnerData = {
            ...data,
            user,
          };

          setShowAddModal(false);

          // Update the partner in the current list
          setPartners((prev) =>
            prev.map((partner) =>
              partner.id === data.id ? partnerData : partner
            )
          );

          // Update cached partner
          await cache.saveOne("partners", partnerData);

        } catch (err: any) {
          console.error("Error updating partner:", err);

          showToast(
            err?.message ||
            "Failed to update partner. Please check your network and try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
        }
      },
      true,
      "Yes, Update",
      "Cancel"
    );
  };


  const deletePartner = async (partner: Partner) => {
    return openModal(
      "Delete Partner",
      `Are you sure you want to delete ${partner.name}? This action cannot be undone.`,
      async () => {
        setLoading(true);

        const cache = await initAllStores();

        try {
          await sendRequest("DELETE", `/partners/${partner.id}`);

          showToast("Partner deleted successfully.", {
            type: "success",
          });

          // Remove from local state
          setPartners((prev) =>
            prev.filter((item) => item.id !== partner.id)
          );

          await cache.deleteOne("partners", partner.id);

        } catch (err: any) {
          console.error("Error deleting partner:", err);

          showToast(
            err?.message ||
            "Failed to delete partner. Please check your network and try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
        }
      },
      true,
      "Yes, Delete",
      "Cancel"
    );
  };


  const handlePartnerAction = (
    action: "view" | "edit" | "delete",
    partner: Partner
  ) => {

    switch (action) {
      case "view": {
        setSelectedPartner(partner);
        setShowDetailModal(true);
        break;
      }

      case "edit": {
        setIsUpdate(true);
        setShowAddModal(true);
        setSelectedPartner({
          ...partner,
          status:
            partner.isActive
              ? "active" : "inactive"
        });
        break;
      }

      case "delete":
        deletePartner(partner);
        break;
    }
  };


  function PartnerRow({
    partner,
  }: {
    partner: Partner;
  }) {
    return (
      <tr className="group transition-colors hover:bg-slate-50/60">

        {/* Partner */}
        <td className="px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 uppercase">
              {getInitials(partner.name)}
            </div>

            <div className="min-w-0 flex-1">
              <p
                title={`${partner.name}`}
                className="truncate text-sm font-semibold text-slate-900"
              >
                {partner.name}
              </p>

              {partner.companyName && (
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {partner.companyName}
                </p>
              )}
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="px-6 py-4">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${typeStyles[partner.type]
              }`}
          >
            {partner.type}
          </span>
        </td>

        {/* Specialty */}
        <td className="px-6 py-4">
          <p
            className="truncate text-sm text-slate-600"
            title={partner.specialty}
          >
            {partner.specialty}
          </p>
        </td>

        {/* Contact */}
        <td className="px-6 py-4">
          <div className="min-w-0 space-y-1.5">

            <div className="flex min-w-0 items-center gap-2 text-xs text-slate-600">
              {FaPhone({
                className: "shrink-0 text-slate-400",
                size: 10
              })}

              <span className="truncate">
                {partner.phone}
              </span>
            </div>

            {partner.email && (
              <div className="flex min-w-0 items-center gap-2 text-xs text-slate-400">
                {FaEnvelope({
                  className: "shrink-0",
                  size: 10
                })}

                <span
                  className="min-w-0 truncate"
                  title={partner.email}
                >
                  {partner.email}
                </span>
              </div>
            )}

          </div>
        </td>

        {/* Location */}
        <td className="max-w-[200px] px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            {FaMapMarkerAlt
              ({
                className: "shrink-0 text-slate-400",
                size: 11
              })}

            <span className="truncate">
              {partner.address}
            </span>
          </div>
        </td>

        {/* Actions */}
        <td className="px-4 py-4 text-right">
          <div className="flex justify-end">
            <PartnerMenu
              partner={partner}
              onAction={handlePartnerAction}
            />
          </div>
        </td>

      </tr>
    );
  }

  function PartnerMobileCard({
    partner,
  }: {
    partner: Partner;
  }) {
    return (
      <div className="p-4 sm:p-5">

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">

          {/* Header */}
          <div className="flex items-start justify-between gap-3">

            <div className="flex min-w-0 items-center gap-3">

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700">
                {getInitials(partner.name)}
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {partner.name}
                </p>

                {partner.companyName && (
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {partner.companyName}
                  </p>
                )}
              </div>
            </div>

            <PartnerMenu
              partner={partner}
              onAction={handlePartnerAction}
            />

          </div>

          {/* Tags */}
          <div className="mt-4 flex flex-wrap items-center gap-2">

            <span
              className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${typeStyles[partner.type]
                }`}
            >
              {partner.type}
            </span>

            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Active
            </span>

          </div>

          {/* Details */}
          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-slate-100 pt-4">

            {/* Specialty */}
            <div className="flex min-w-0 items-start gap-2.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                {FaBriefcase({ size: 11 })}
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Specialty
                </p>
                <p className="mt-0.5 truncate text-sm text-slate-700">
                  {partner.specialty}
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex min-w-0 items-start gap-2.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                {FaPhone({ size: 11 })}
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Phone
                </p>

                <a
                  href={`tel:${partner.phone}`}
                  className="mt-0.5 block truncate text-sm text-slate-700 hover:underline"
                >
                  {partner.phone}
                </a>
              </div>
            </div>

            {/* Email */}
            {partner.email && (
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                  {FaEnvelope({ size: 11 })}
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    Email
                  </p>

                  <a
                    href={`mailto:${partner.email}`}
                    className="mt-0.5 block truncate text-sm text-slate-700"
                  >
                    {partner.email}
                  </a>
                </div>
              </div>
            )}

            {/* Location */}
            <div className="col-span-2 flex min-w-0 items-start gap-2.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                {FaMapMarkerAlt({ size: 11 })}
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Location
                </p>
                <p className="mt-0.5 truncate text-sm text-slate-700">
                  {partner.address}
                </p>
              </div>
            </div>

          </div>

        </div>
      </div>
    );
  }

  function AddPartnerModal({
    onClose,
    onSubmit,
    isUpdate = false,
    partner = null,
  }: {
    onClose: () => void;
    onSubmit: (data: Partner) => void;
    isUpdate?: boolean;
    partner?: Partner | null;
  }) {
    const [loading, setLoading] = useState<boolean>(false);

    const [formData, setFormData] = useState<Partner>(partner || {
      id: uuidv4(),
      name: "",
      companyName: "",
      type: "artisan" as PartnerType,
      phone: "",
      email: "",
      address: "",
      specialty: "",
      description: "",
      status: "active"
    });

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      const { status, ...withoutStatus } = formData;
      const data = {
        ...withoutStatus,
        isActive:
          status?.toLowerCase() === "active",
        type:
          withoutStatus?.type?.toLocaleLowerCase() as PartnerType
      }

      onSubmit(data);
      onClose();
    };

    const title = isUpdate ? "Update" : "Add";

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">

        <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">

          <FullPageSpinner show={loading} />

          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">

            <div>

              <h2 className="text-lg font-semibold text-slate-900">
                {title} Partner
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                {title} an artisan, consultant, vendor or external professional.
              </p>

            </div>

            <button
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              {FaTimes({ size: 15 })}
            </button>

          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 p-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Partner Type
                </label>

                <div className="grid grid-cols-3 gap-2">

                  {(
                    ["artisan", "consultant", "vendor"] as PartnerType[]
                  ).map((item) => (

                    <button
                      key={item}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          type: item,
                        }))
                      }
                      className={`rounded-lg border px-3 py-3 text-sm font-medium transition ${formData.type === item
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      {capitalizeFirst(item)}
                    </button>

                  ))}

                </div>
              </div>

              <div>

                <div className="grid gap-4 sm:grid-cols-2">

                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName ?? ""}
                    onChange={handleChange}
                  />

                  <Input
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                  />

                  <Input
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Specialty / Service"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>

                <div className="grid gap-4 sm:grid-cols-2">


                  {/* <Input label="Registration Number" /> */}

                  <div className="sm:col-span-2">

                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Description
                    </label>

                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Brief description of services..."
                      className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                    />

                  </div>

                </div>

              </div>

              <div>

                <h3 className="mb-4 text-sm font-semibold text-slate-900">
                  Status
                </h3>

                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-4">

              <button
                onClick={onClose}
                className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button className="h-10 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700">
                {title} Partner
              </button>

            </div>
          </form>

        </div>

      </div>

    );
  }

  return (
    <div className="min-h-screen min-w-0 bg-slate-50 text-slate-900">
      <main className="w-full min-w-0 px-4 py-6 sm:px-6 lg:px-6">
        <FullPageSpinner show={loading} />


        {/* Header */}
        <div className="mb-7 flex flex-row items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">
              Partners
            </h1>


          </div>

          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            {FaPlus({ size: 14 })}
            <span>Add Partner</span>
          </button>
        </div>

        {selectedPartner && showDetailModal && (
          <PartnerDetailsModal
            partner={selectedPartner}
            onClose={() => {
              setSelectedPartner(null);
              setShowDetailModal(false);

            }}
          />
        )}
        {/* Overview Cards */}
        <div className="mb-6">

          {/* Cards */}
          {/* {showStats && ( */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Partners"
              value={String(partners.length)}
              // subtitle="All registered partners"
              icon={FaUsers({})}
            />

            <StatCard
              title="Artisans"
              value={String(
                partners.filter((partner) => partner.type === "artisan").length
              )}
              // subtitle="Registered artisans"
              icon={FaHardHat({})}
            />

            <StatCard
              title="Consultants"
              value={String(
                partners.filter((partner) => partner.type === "consultant").length
              )}
              // subtitle="Registered consultants"
              icon={FaBriefcase({})}
            />

            <StatCard
              title="Vendors"
              value={String(
                partners.filter((partner) => partner.type === "vendor").length
              )}
              // subtitle="Registered vendors"
              icon={FaStore({})}
            />
          </div>
          {/* )} */}
        </div>


        {/* Main Card */}
        <section className="flex min-h-[calc(100vh-180px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

          {/* Toolbar */}
          {partners.length > 0 && <div className="border-b border-slate-200 p-4 sm:p-5">
            <div className="relative w-full">


              <div className="relative w-full min-w-0">
                <div
                  className="
      flex min-w-0 w-full items-center gap-2
      rounded-xl border border-gray-200 bg-white
      px-3.5 pr-12
      focus-within:border-indigo-500
      focus-within:ring-2 focus-within:ring-indigo-100
    "
                >
                  {/* Search icon */}
                  <div className="shrink-0 text-slate-400">
                    {FaSearch({ size: 14 })}
                  </div>

                  {/* Search content */}
                  <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                    {filter.type !== "all" && (
                      <div className="min-w-0 shrink-0">
                        <FilterChip
                          label={
                            filter.type.length > 3
                              ? `${filter.type.slice(0, 3)}...`
                              : filter.type
                          }
                          onRemove={() => {
                            setFilter((prev) => ({
                              ...prev,
                              type: "all",
                            }));
                          }}
                        />
                      </div>
                    )}

                    {filter.status !== "all" && (
                      <div className="min-w-0 shrink-0">
                        <FilterChip
                          label={
                            filter.status.length > 3
                              ? `${filter.status.slice(0, 3)}...`
                              : filter.status
                          }
                          onRemove={() => {
                            setFilter((prev) => ({
                              ...prev,
                              status: "all",
                            }));
                          }}
                        />
                      </div>
                    )}

                    <input
                      type="text"
                      placeholder="Search partners..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="
          min-w-0 flex-1
          border-0 bg-transparent
          py-2 text-sm
          outline-none
          placeholder:text-slate-400
          focus:ring-0
        "
                    />
                  </div>

                  {/* Filter button */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowFilters((prev) => !prev);
                      setActiveFilterMenu(null);
                    }}
                    className={`
        absolute right-1.5 top-1/2
        flex h-8 w-8 shrink-0
        -translate-y-1/2
        items-center justify-center
        rounded-lg transition
        ${showFilters
                        ? "bg-indigo-50 text-indigo-600"
                        : "text-slate-400 hover:bg-slate-100 hover:text-indigo-600"
                      }
      `}
                    title="Filter"
                  >
                    {FaSlidersH({ size: 14 })}
                  </button>
                </div>

                {/* Filter dropdown */}
                {showFilters && (
                  <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
                    {/* filters */}
                  </div>
                )}
              </div>

              {/* Filter Menu */}
              {showFilters && (
                <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 overflow-visible rounded-xl border border-gray-200 bg-white p-2 shadow-xl">

                  {/* Type */}
                  <FilterMenuItem
                    label="Type"
                    value={filter.type === "all" ? "All Type" : filter.type}
                    open={activeFilterMenu === "type"}
                    onClick={() =>
                      setActiveFilterMenu(
                        activeFilterMenu === "type" ? null : "type"
                      )
                    }
                  />

                  {activeFilterMenu === "type" && (
                    <div className="mt-1 rounded-lg bg-gray-50 p-2">
                      {[
                        "all",
                        "artisan",
                        "consultant",
                        "vendor",
                      ].map(type => (
                        <FilterOption
                          key={type}
                          label={type === "all" ? "All Types" : capitalizeFirst(type)}
                          active={filter.type === type}
                          onClick={() => {
                            setFilter((prev) => ({
                              ...prev,
                              type,
                            }));

                            setActiveFilterMenu(null);
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Status */}
                  <FilterMenuItem
                    label="Status"
                    value={filter.status === "all" ? "All Statuss" : filter.status}
                    open={activeFilterMenu === "status"}
                    onClick={() =>
                      setActiveFilterMenu(
                        activeFilterMenu === "status" ? null : "status"
                      )
                    }
                  />

                  {activeFilterMenu === "status" && (
                    <div className="mt-1 rounded-lg bg-gray-50 p-2">
                      {[
                        "all",
                        "active",
                        "inactive"
                      ].map(status => (
                        <FilterOption
                          key={status}
                          label={status === "all" ? "All Status" : capitalizeFirst(status)}
                          active={filter.status === status}
                          onClick={() => {
                            setFilter((prev) => ({
                              ...prev,
                              status
                            }));

                            setActiveFilterMenu(null);
                          }}
                        />
                      ))}
                    </div>
                  )}


                  {/* Date */}
                  <FilterMenuItem
                    label="Date"
                    value={getDateLabel(filter.date)}
                    open={activeFilterMenu === "date"}
                    onClick={() =>
                      setActiveFilterMenu(
                        activeFilterMenu === "date" ? null : "date"
                      )
                    }
                  />

                  {activeFilterMenu === "date" && (
                    <div className="mt-1 rounded-lg bg-gray-50 p-2">
                      <FilterOption
                        label="Today"
                        active={filter.date === "today"}
                        onClick={() => {
                          setFilter(prev => ({ ...prev, date: "today" }));
                          setDateRange({ from: "", to: "" });
                          setActiveFilterMenu(null);
                        }}
                      />

                      <FilterOption
                        label="Yesterday"
                        active={filter.date === "yesterday"}
                        onClick={() => {
                          setFilter(prev => ({
                            ...prev,
                            date: "yesterday",
                          }));
                          setDateRange({ from: "", to: "" });
                          setActiveFilterMenu(null);
                        }}
                      />

                      <FilterOption
                        label="This Week"
                        active={filter.date === "week"}
                        onClick={() => {
                          setFilter(prev => ({ ...prev, date: "week" }));
                          setDateRange({ from: "", to: "" });
                          setActiveFilterMenu(null);
                        }}
                      />

                      <FilterOption
                        label="This Month"
                        active={filter.date === "month"}
                        onClick={() => {
                          setFilter(prev => ({ ...prev, date: "month" }));
                          setDateRange({ from: "", to: "" });
                          setActiveFilterMenu(null);
                        }}
                      />

                      <FilterOption
                        label="This Year"
                        active={filter.date === "year"}
                        onClick={() => {
                          setFilter(prev => ({ ...prev, date: "year" }));
                          setDateRange({ from: "", to: "" });
                          setActiveFilterMenu(null);
                        }}
                      />

                      {/* Custom Range */}
                      <div className="mt-2 border-t border-gray-200 pt-2">
                        <p className="mb-2 px-2 text-xs font-medium text-gray-500">
                          Custom Range
                        </p>

                        <div className="space-y-2">
                          <input
                            type="date"
                            value={dateRange.from}
                            onChange={(e) =>
                              setDateRange(prev => ({
                                ...prev,
                                from: e.target.value,
                              }))
                            }
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                          />

                          <input
                            type="date"
                            value={dateRange.to}
                            onChange={(e) =>
                              setDateRange(prev => ({
                                ...prev,
                                to: e.target.value,
                              }))
                            }
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                          />

                          <button
                            disabled={!dateRange.from || !dateRange.to}
                            onClick={() => {
                              setFilter(prev => ({
                                ...prev,
                                date: "custom",
                              }));
                              setActiveFilterMenu(null);
                            }}
                            className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Apply Range
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Clear */}
                  <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
                    <button
                      type="button"
                      onClick={() => {
                        // Apply filters
                        setShowFilters(false);
                        setActiveFilterMenu(null);
                      }}
                      className="flex-1 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    >
                      Apply
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setFilter({
                          date: "all",
                          type: "all",
                          status: "all"
                        });
                        // setShowFilters(false);

                        setDateRange({
                          from: "",
                          to: "",
                        });


                        setSearch("")
                        setActiveFilterMenu(null);
                      }}
                      className="flex-1 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>}

          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[950px] table-fixed">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Partner
                    </th>

                    <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Type
                    </th>

                    <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Specialty
                    </th>

                    <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Contact
                    </th>

                    <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Location
                    </th>

                    <th className="w-12 px-4 py-3.5" />
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filteredPartners.map((partner) => (
                    <PartnerRow
                      key={partner.id}
                      partner={partner}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-100 md:hidden">

            {filteredPartners.map((partner) => (
              <PartnerMobileCard
                key={partner.id}
                partner={partner}
              />
            ))}

          </div>

          {/* Empty State */}
          {filteredPartners.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                {FaUsers({ size: 19 })}
              </div>

              <h3 className="text-sm font-semibold text-slate-900">
                No partners found
              </h3>

              <p className="mt-1 max-w-sm text-sm text-slate-500">
                Try changing your search or selected partner category.
              </p>
            </div>
          )}

          {/* PAGINATION BUTTON */}
          {filteredPartners.length > itemsPerPage && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filteredPartners.length}
              itemsPerPage={itemsPerPage}
              itemLabel="students"
              visiblePages={3}
              hasPreviousPage={page > 1}
              hasNextPage={page < totalPages}
              onPrevious={() => setPage((prev) => prev - 1)}
              onNext={() => setPage((prev) => prev + 1)}
              onPageChange={setPage}
            />
          )}
        </section>
      </main>

      {showAddModal && (
        <AddPartnerModal
          onClose={() => setShowAddModal(false)}
          isUpdate={isUpdate}
          partner={selectedPartner}
          onSubmit={!isUpdate ? addPartner : updatePartner}
        />
      )}
    </div>
  );
}


export default Partners;